import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  LayoutDashboard,
  Building2,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Bell,
  ChevronDown,
  CreditCard,
  Headphones,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Logo, LogoIcon } from '../components/Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { getMSPPermissions } from '../services/api';

// Role-based navigation permissions
const rolePermissions = {
  msp_admin: ['dashboard', 'clients', 'team', 'earnings', 'settings', 'support'],
  msp_technician: ['dashboard', 'clients', 'support'],
  msp_support: ['support'],
  msp_billing: ['dashboard', 'earnings'],
};

const allNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/msp', permission: 'dashboard' },
  { icon: Building2, label: 'Clients', path: '/msp/clients', permission: 'clients' },
  { icon: Headphones, label: 'Support', path: '/msp/support', permission: 'support' },
  { icon: Users, label: 'Team', path: '/msp/team', permission: 'team' },
  { icon: DollarSign, label: 'Earnings', path: '/msp/earnings', permission: 'earnings' },
  { icon: Settings, label: 'Settings', path: '/msp/settings', permission: 'settings' },
];

// Get role display name
const getRoleDisplayName = (role) => {
  const roleNames = {
    msp_admin: 'Admin',
    msp_technician: 'Technician',
    msp_support: 'Support',
    msp_billing: 'Billing',
  };
  return roleNames[role] || role?.replace('msp_', '');
};

export default function MSPLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, msp, logout } = useAuth();
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await getMSPPermissions();
        setPermissions(response.data.permissions || []);
      } catch (error) {
        // Fallback to role-based permissions from local user object
        const userRole = user?.role || 'msp_support';
        setPermissions(rolePermissions[userRole] || []);
      }
    };
    
    fetchPermissions();
  }, [user?.role]);

  // Filter nav items based on user permissions
  const navItems = allNavItems.filter(item => permissions.includes(item.permission));

  const handleLogout = () => {
    logout();
    navigate('/msp/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <Link to="/msp" className="flex items-center gap-3">
            <LogoIcon size="lg" />
            <div>
              <span className="font-bold text-lg text-white">Clear Defense AI</span>
              <Badge className="ml-2 bg-purple-500/20 text-purple-400 text-xs">MSP</Badge>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/msp' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-blue-500/10 text-blue-400'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* MSP Info */}
        <div className="p-4 border-t border-slate-800">
          <div className="p-4 rounded-lg bg-slate-800/50">
            <p className="text-xs text-slate-500 mb-1">MSP ACCOUNT</p>
            <p className="text-sm font-medium text-white truncate">{msp?.company_name}</p>
            <div className="flex items-center gap-2 mt-2">
              {msp?.stripe_connected ? (
                <Badge className="bg-emerald-500/10 text-emerald-400 text-xs">
                  <CreditCard className="w-3 h-3 mr-1" />
                  Stripe Connected
                </Badge>
              ) : (
                <Badge className="bg-amber-500/10 text-amber-400 text-xs">
                  Connect Stripe
                </Badge>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="h-16 border-b border-slate-800 flex items-center justify-between px-6 bg-slate-900/50">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-white">MSP Portal</h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="w-5 h-5 text-slate-400" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                3
              </span>
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-400">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{getRoleDisplayName(user?.role)}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem
                  onClick={() => navigate('/msp/settings')}
                  className="text-slate-300 cursor-pointer"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-400 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
